/**
 * RYZE Cart Toast — iOS 26 Liquid Glass Notification
 * ─────────────────────────────────────────────────────
 * Provides a globally-available toast notification that fires
 * on every successful add-to-cart, regardless of the source
 * (PDP form, quick-add card, sticky ATC bar).
 *
 * Design: Apple iOS 26 liquid glass — frosted translucent
 * capsule with subtle refraction, backdrop-blur, and a
 * smooth spring animation.
 *
 * Usage:
 *   window.RyzeToast.show('Malachite', 'added');
 *   window.RyzeToast.show('2 items', 'updated');
 *   window.RyzeToast.show('Item', 'removed');
 *   window.RyzeToast.show('Only 2 left in stock.', 'error');
 *
 * 'error' (fixes 2026-10-18, CODE-11): a refused or failed add. The first
 * argument is the reason, shown in full (it may wrap); the action line reads
 * "Not added to bag", the ring is ruby and View Cart steps aside.
 *
 * ONE TOAST FAMILY (dev2.15-fix-cart, 30 Oct 2026):
 *   window.RyzeToast.show('Nova', 'added', 'Added 1, the last we have');
 *     the optional third argument replaces the action line (a partial add
 *     says what landed; ryze-pcard.js);
 *   window.RyzeToast.show('Como', 'saved');    "Saved to wishlist", heart
 *   window.RyzeToast.show('Como', 'unsaved');  "Removed from wishlist"
 *     the wishlist speaks in this capsule (it had its own ink pill), its
 *     button reads View wishlist; a wishlist toast that lands while another
 *     wishlist toast is up replaces it in place (a heart tapped on, off, on
 *     is one answer, not three queued ones); behind any other toast at
 *     most one wishlist answer waits (the newest), and an in-place answer
 *     keeps the running clock while another toast waits.
 *
 * THE BUTTON STEPS ASIDE WHERE IT WOULD LAND (dev2.15-calls-cart, 31 Oct
 * 2026; Sawy's 'all as recommended'): View wishlist is not offered on
 * /pages/wishlist and View Cart is not offered on /cart (on its own page the
 * button only reloaded the page the shopper is reading); the capsule keeps
 * its icon, its name and its line, as an error toast already does.
 *
 * The toast auto-dismisses after 3s and queues if called
 * while one is already visible.
 */
(function () {
  'use strict';
  if (window.RyzeToast) return;

  /* ── SVG Icons ─────────────────────────────────────────── */
  var ICON_CHECK =
    '<svg class="ryze-ctoast__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 6 9 17 4 12"/>' +
    '</svg>';

  var ICON_BAG =
    '<svg class="ryze-ctoast__icon-svg ryze-ctoast__icon-bag" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>' +
      '<line x1="3" y1="6" x2="21" y2="6"/>' +
      '<path d="M16 10a4 4 0 01-8 0"/>' +
    '</svg>';

  var ICON_REMOVE =
    '<svg class="ryze-ctoast__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="18" y1="6" x2="6" y2="18"/>' +
      '<line x1="6" y1="6" x2="18" y2="18"/>' +
    '</svg>';

  var ICON_HEART =
    '<svg class="ryze-ctoast__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>' +
    '</svg>';

  var ICON_ALERT =
    '<svg class="ryze-ctoast__icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="12" y1="6" x2="12" y2="14"/>' +
      '<line x1="12" y1="18" x2="12.01" y2="18"/>' +
    '</svg>';

  /* ── Toast State ───────────────────────────────────────── */
  var container = null;
  var dismissTimer = null;
  var queue = [];
  var isVisible = false;
  var current = null; /* the action on screen */
  var leaving = false; /* between dismiss() and the exit's end: never re-rendered in place */

  function isWishlist(action) { return action === 'saved' || action === 'unsaved'; }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  }
  /* the page the button would open, for this action */
  function targetPath(action) {
    return routeRoot() + (isWishlist(action) ? 'pages/wishlist' : 'cart');
  }
  /* true when the shopper already stands on that page (a trailing slash and
     the case of the path do not count) */
  function isHere(action) {
    var norm = function (p) { return String(p || '').replace(/\/+$/, '').toLowerCase() || '/'; };
    try { return norm(window.location.pathname) === norm(targetPath(action)); } catch (e) { return false; }
  }

  /* ── Create DOM (once) ─────────────────────────────────── */
  function ensureDOM() {
    if (container) return;

    container = document.createElement('div');
    container.className = 'ryze-ctoast';
    container.setAttribute('role', 'status');
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');

    container.innerHTML =
      '<div class="ryze-ctoast__glass">' +
        '<div class="ryze-ctoast__icon-ring">' +
          '<span class="ryze-ctoast__icon"></span>' +
        '</div>' +
        '<div class="ryze-ctoast__body">' +
          '<span class="ryze-ctoast__product"></span>' +
          '<span class="ryze-ctoast__action"></span>' +
        '</div>' +
        '<button class="ryze-ctoast__view-cart" type="button">View Cart</button>' +
        '<div class="ryze-ctoast__progress"></div>' +
      '</div>';

    document.body.appendChild(container);

    // View Cart button (View wishlist while a wishlist toast is up)
    container.querySelector('.ryze-ctoast__view-cart').addEventListener('click', function () {
      window.location.href = targetPath(current);
    });

    // Dismiss on click anywhere on toast
    container.querySelector('.ryze-ctoast__glass').addEventListener('click', function (e) {
      if (e.target.closest('.ryze-ctoast__view-cart')) return;
      dismiss();
    });
  }

  /* ── Position above sticky ATC bar if visible ───────────── */
  function updatePosition() {
    if (!container) return;
    var stickyBar = document.querySelector('.ryze-sticky-atc.visible');
    if (stickyBar) {
      // W7 2026-10-18: the bar is a full-width docked bar (no capsule, no outer
      // padding); its own height already carries the safe-area inset.
      var barHeight = stickyBar.offsetHeight || 95;
      // 14px = clearance between bar and toast
      container.style.setProperty('--ryze-ctoast-bottom', (barHeight + 14) + 'px');
    } else {
      // Default floating position: 32px above the home indicator (THE SAFE
      // AREA, dev2.14-safe; env() is 0 off iOS, so every other screen keeps 32px)
      container.style.setProperty('--ryze-ctoast-bottom', 'calc(32px + env(safe-area-inset-bottom, 0px))');
    }
  }

  /* ── Show Toast ────────────────────────────────────────── */
  function show(productName, action, detail) {
    ensureDOM();

    // If already visible, queue it (a wishlist answer over a wishlist answer
    // replaces it in place: ONE TOAST FAMILY, dev2.15-fix-cart)
    if (isVisible) {
      if (isWishlist(action) && isWishlist(current) && !leaving) {
        /* in place; with a toast waiting behind it, its clock is not
           restarted, so hearts can never hold a cart answer back */
        render(productName, action, detail, queue.length > 0);
        return;
      }
      if (isWishlist(action)) {
        /* one wishlist answer waits at most: the newest replaces it */
        for (var qi = queue.length - 1; qi >= 0; qi--) {
          if (isWishlist(queue[qi].action)) {
            queue[qi] = { product: productName, action: action, detail: detail };
            return;
          }
        }
      }
      queue.push({ product: productName, action: action, detail: detail });
      return;
    }

    isVisible = true;
    render(productName, action, detail);
  }

  function render(productName, action, detail, keepClock) {
    current = action;

    // Position toast above sticky bar if present
    updatePosition();

    // Set content
    var iconEl = container.querySelector('.ryze-ctoast__icon');
    var productEl = container.querySelector('.ryze-ctoast__product');
    var actionEl = container.querySelector('.ryze-ctoast__action');
    var ringEl = container.querySelector('.ryze-ctoast__icon-ring');
    var progressEl = container.querySelector('.ryze-ctoast__progress');

    // Determine action text and icon
    var actionText = 'Added to bag';
    var icon = ICON_CHECK;
    ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--added';

    if (action === 'removed') {
      actionText = 'Removed from bag';
      icon = ICON_REMOVE;
      ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--removed';
    } else if (action === 'updated') {
      actionText = 'Cart updated';
      icon = ICON_BAG;
      ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--updated';
    } else if (action === 'error') {
      actionText = 'Not added to bag';
      icon = ICON_ALERT;
      ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--removed';
    } else if (action === 'saved') {
      actionText = 'Saved to wishlist';
      icon = ICON_HEART;
      ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--added';
    } else if (action === 'unsaved') {
      actionText = 'Removed from wishlist';
      icon = ICON_REMOVE;
      ringEl.className = 'ryze-ctoast__icon-ring ryze-ctoast__icon-ring--removed';
    }
    if (typeof detail === 'string' && detail) actionText = detail;
    container.classList.toggle('ryze-ctoast--error', action === 'error');
    container.classList.toggle('ryze-ctoast--here', isHere(action));

    iconEl.innerHTML = icon;
    productEl.textContent = productName;
    actionEl.textContent = actionText;
    var ctaEl = container.querySelector('.ryze-ctoast__view-cart');
    if (ctaEl) ctaEl.textContent = isWishlist(action) ? 'View wishlist' : 'View Cart';

    if (keepClock) return; /* the content changed in place; the running clock and its bar stand */

    // Reset and trigger progress bar
    progressEl.style.transition = 'none';
    progressEl.style.transform = 'scaleX(1)';
    void progressEl.offsetWidth; // force reflow
    progressEl.style.transition = 'transform 3s linear';
    progressEl.style.transform = 'scaleX(0)';

    // Animate in
    container.classList.remove('ryze-ctoast--out');
    container.classList.add('ryze-ctoast--in');

    // Schedule dismiss
    clearTimeout(dismissTimer);
    dismissTimer = setTimeout(dismiss, 3000);
  }

  /* ── Dismiss Toast ─────────────────────────────────────── */
  function dismiss() {
    if (!container || !isVisible || leaving) return;
    clearTimeout(dismissTimer);
    leaving = true;

    container.classList.remove('ryze-ctoast--in');
    container.classList.add('ryze-ctoast--out');

    setTimeout(function () {
      isVisible = false;
      leaving = false;
      container.classList.remove('ryze-ctoast--out');

      // Process queue
      if (queue.length > 0) {
        var next = queue.shift();
        show(next.product, next.action, next.detail);
      }
    }, 400); // match exit animation duration
  }

  /* ── Public API ────────────────────────────────────────── */
  window.RyzeToast = {
    show: show,
    dismiss: dismiss
  };
})();